<!--
  Action dialog "Berater wechseln" — writes on ONE Provisionssatz (cp.ProvisionContractAgent),
  not on a contract; see the class TSDoc for what moved in WP-E14.

  IMPORTANT: This file contains NO form HTML. Target Berater and reason are rendered by the
  transient form widget built from the MvsFormDto in the TS — repo precedent
  wf-process-start.component.html, identical to the sibling dialogs. Only the dialog
  frame, the mandatory consequence preview, and the action buttons live here.
-->
<p-dialog
        header="Berater wechseln"
        [visible]="visible"
        (visibleChange)="handleVisibleChange($event)"
        (onShow)="handleShow()"
        [modal]="true"
        [draggable]="false"
        [resizable]="false"
        [style]="{width: '48rem'}"
        [breakpoints]="{'960px': '90vw'}"
        styleClass="cp-provision-agent-change-dialog">

    @if (busy && !formWidget) {

        <p-skeleton height="16rem"></p-skeleton>

    } @else if (formWidget) {

        <!-- Context: which Provisionssatz, which Abrechnungskonto so far -->
        <div class="flex flex-wrap align-items-center gap-2 mb-3">
            <span class="text-color-secondary text-sm">Provisionssatz</span>
            <span class="font-medium">{{ provisionContractAgentLabel ?? ('#' + provisionContractAgentId) }}</span>

            @if (currentProvisionAgentName) {
                <i class="fa-regular fa-user text-color-secondary ml-2"></i>
                <span class="text-color-secondary text-sm">bisher</span>
                <span class="font-medium">{{ currentProvisionAgentName }}</span>
            }
        </div>

        <!-- The dialog opens with the action available (optimistic, see the class TSDoc); this
             banner appears only after the SERVER refused with a 403. The reason is shown visibly
             in the dialog, not only in the tooltip. -->
        @if (!canAgentChange) {
            <p-message severity="warn" styleClass="w-full mb-3">
                <span>
                    Der Server hat den Wechsel verweigert: Ihnen fehlt das Recht
                    <strong>cp_agentChange</strong>. Der Wechsel kann angesehen, aber nicht
                    abgeschickt werden.
                </span>
            </p-message>
        }

        <!-- Capture: metadata-driven form widget -->
        <mvs-widget
                [widgetData]="formWidget"
                (onGenericEvent)="handleGenericEvent($event)">
        </mvs-widget>

        <!-- Mandatory preview of the reconcile consequences (01 §7, BR-016) -->
        <div class="surface-100 border-round p-3 mt-3">

            <div class="font-semibold mb-2">Vorschau der entstehenden Buchungen</div>

            @if (previewState == 'idle') {

                <span class="text-color-secondary text-sm">
                    Neuen Berater wählen, um die Folgen des Wechsels zu sehen.
                </span>

            } @else if (previewState == 'loading') {

                <p-skeleton height="6rem"></p-skeleton>

            } @else if (previewState == 'failed') {

                <p-message severity="error" styleClass="w-full">
                    <span>
                        Die Vorschau konnte nicht geladen werden. Ohne Vorschau lässt sich der
                        Wechsel nicht abschicken.
                    </span>
                </p-message>

            } @else if (!consequences.length) {

                <p-message severity="info" styleClass="w-full">
                    <span>
                        Für diesen Provisionssatz sind keine aktiven Buchungen betroffen. Der nächste
                        Berechnungslauf bucht ausschließlich auf
                        {{ targetAgentName ?? 'den neuen Berater' }}.
                    </span>
                </p-message>

            } @else {

                <div class="flex flex-column gap-2">
                    @for (consequence of consequences; track consequence.description) {
                        <div class="surface-card border-round p-3 flex flex-wrap align-items-center gap-3">

                            @if (consequence.affectsPaidOutBookings) {
                                <p-tag value="Bereits ausgezahlt" severity="warn"></p-tag>
                            }

                            <span class="flex-1">{{ consequence.description }}</span>

                            <span class="font-semibold">{{ consequence.affectedCount }}</span>
                            <span class="text-color-secondary text-sm">Buchungen</span>

                        </div>
                    }
                </div>

                <div class="flex align-items-center gap-2 mt-3">
                    <i class="fa-regular fa-arrow-right-arrow-left text-color-secondary"></i>
                    <span class="text-color-secondary text-sm">
                        Insgesamt {{ affectedRowCount }} Buchungen werden mit dem nächsten
                        Berechnungslauf auf
                        <strong>{{ targetAgentName ?? 'den neuen Berater' }}</strong> umgehängt
                        (Original-Valuta, Rücklage wird beim neuen Berater neu gekappt).
                    </span>
                </div>

                @if (affectsPaidOutBookings) {
                    <p-message severity="warn" styleClass="w-full mt-3">
                        <span>
                            <i class="fa-regular fa-triangle-exclamation mr-2"></i>
                            Bereits <strong>ausgezahlte</strong> Buchungen sind betroffen. Beim
                            bisherigen Berater entsteht dadurch eine offene Forderung, die mit
                            künftigen Läufen verrechnet wird. Vor dem Schreiben wird noch einmal
                            ausdrücklich bestätigt.
                        </span>
                    </p-message>
                }

            }

        </div>

        @if (failureMessage) {
            <p-message severity="error" [text]="failureMessage" styleClass="w-full mt-3"></p-message>
        }

    }

    <ng-template pTemplate="footer">
        <p-button
                label="Abbrechen"
                icon="fa-regular fa-xmark"
                severity="secondary"
                [text]="true"
                (onClick)="handleClose()">
        </p-button>

        <p-button
                label="Berater wechseln"
                icon="fa-regular fa-check"
                severity="primary"
                [loading]="busy"
                [disabled]="!submittable"
                [pTooltip]="submitTooltip"
                tooltipPosition="top"
                (onClick)="handleChangeAgent()">
        </p-button>
    </ng-template>

</p-dialog>
